import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
} from '@angular/core';
import { TtoLeadsFromDb } from '../../../interfaces/responsedata.ts/mentor';
import { MentorService } from '../../../service/mentor.service';
import { DbLicense } from '../../../interfaces/responsedata.ts/license';
import { UserdataService } from '../../../service/userdata.service';
import { ConfirmDialog } from '../../../common/interfaces/confirm-dialog.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, finalize, from, switchMap } from 'rxjs';
import { DialogService } from '../../../service/dialog.service';

@Component({
    selector: 'app-invitee',
    standalone: false,
    templateUrl: './invitee.component.html',
    styleUrl: './invitee.component.scss',
})
export class InviteeComponent {
    @Input({ required: true })
    public invitee!: TtoLeadsFromDb;

    @Input({ required: true })
    public licenseData: DbLicense[] = [];

    @Output()
    public acceptedInvite = new EventEmitter<string>();

    @Output()
    public isLoading = new EventEmitter<boolean>(false);

    @Output()
    public declinedLeadId = new EventEmitter<string>();

    private mentorService = inject(MentorService);
    private userdataService = inject(UserdataService);
    private dialogService = inject(DialogService);
    private destroyRef = inject(DestroyRef);

    protected async activateUser(lead: TtoLeadsFromDb): Promise<void> {
        const dialogData: ConfirmDialog = {
            headerText: 'Grant access?',
            dialogText:
                'Are you sure you want to grant access to this user? This reduce your own/company licence.',
            submitText: 'Yes',
            cancelText: 'No, decline',
        };

        this.dialogService
            .openConfirmationDialog$(dialogData)
            .pipe(
                switchMap((confirm) => {
                    this.isLoading.next(true);

                    if (!confirm) {
                        return EMPTY;
                    }

                    return from(
                        this.mentorService.grantAccessForUserInvitation(
                            lead.id,
                        ),
                    );
                }),
                finalize(() => this.isLoading.next(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (result: boolean) => {
                    if (!result) {
                        return;
                    }

                    this.acceptedInvite.next(lead.id);

                    // reduce userlicense by one
                    if (this.licenseData[0].freeSeats !== -1) {
                        // -1 for unlimited Seats
                        this.licenseData[0].freeSeats -= 1;
                        this.userdataService.updateLicenceData(
                            this.licenseData,
                        );
                        // update total active founders number
                    }
                },
            });
    }

    protected async declineInvite(leadId: string): Promise<void> {
        const dialogData: ConfirmDialog = {
            headerText: 'Decline invitee?',
            dialogText: 'Are you sure that you want to decline this invitee?',
            submitText: 'Yes, decline',
            cancelText: 'Cancel',
        };

        this.dialogService
            .openConfirmationDialog$(dialogData)
            .pipe(
                switchMap((confirm) => {
                    this.isLoading.next(true);

                    if (!confirm) {
                        return EMPTY;
                    }

                    return from(
                        this.mentorService.declineUserInvitation(leadId),
                    );
                }),
                finalize(() => this.isLoading.next(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => this.declinedLeadId.next(leadId),
            });
    }
}
